import api from '@/lib/api';
import type { Availability } from '@/types/tournament.model';
import type { ManagerGender } from '@/schemas/managerRegisterPairSchema';

/**
 * Deletes a registration by its ID
 * @param registrationId - The ID of the registration to delete
 * @returns Promise that resolves when the deletion is successful
 */
export const deleteRegistration = async (
  registrationId: string,
): Promise<void> => {
  const response = await api.delete(`/registrations/${registrationId}/delete`);
  return response.data;
};

export type ManagerNewUserPayload = {
  email: string;
  name: string;
  gender: ManagerGender;
  birthdate?: string; // ISO date (YYYY-MM-DD); requerido cuando el torneo tiene restriccion de edad
};

export type ManagerRegisterPlayerPayload = {
  email: string;
  newUser?: ManagerNewUserPayload;
  // Required when newUser is sent, and also when the account already exists but has no Elo for the
  // tournament's sport (a player who never answered the level questions in the app).
  categoryId?: number;
};

export type ManagerRegisterTeamPayload = {
  players: ManagerRegisterPlayerPayload[];
  availability?: Availability[];
};

export const managerRegisterTeam = async (
  tournamentId: string,
  payload: ManagerRegisterTeamPayload,
): Promise<void> => {
  // No leemos nada del response: el modal cierra y el page refetcha el torneo.
  // Sin esto, ALL_FIELDS serializa el RegistrationDTO entero (incluida availability + relaciones)
  // y Squiggly tropieza con ciclos.
  await api.post(`/tournaments/${tournamentId}/register-team`, payload, {
    params: { fields: 'id' },
  });
};

/**
 * The manager replaces a player in another team's registration with an existing user.
 * The backend rejects with 400 if the outgoing player already played tournament matches,
 * or if the incoming player doesn't meet the tournament restrictions (gender/category/age).
 *
 * `fields: 'id'` keeps the full RegistrationDTO (with relations/cycles) from being
 * serialized back — same approach as managerRegisterTeam.
 */
export const replacePlayerAsManager = async (
  tournamentId: string,
  registrationId: string,
  currentPlayerEmail: string,
  newPlayerEmail: string,
): Promise<void> => {
  await api.patch(
    `/tournaments/${tournamentId}/registrations/${registrationId}/replace-player`,
    null,
    { params: { currentPlayerEmail, newPlayerEmail, fields: 'id' } },
  );
};

/**
 * The manager overwrites a team's availability (pairs usually ask the organizer by WhatsApp).
 * The backend replaces the whole set and rejects with 400 once the fixture is generated, for a
 * cancelled registration, or for dates outside the tournament. Unlike the player's own
 * `PUT /tournaments/{id}/availability` it skips the tournament's minimum of slots, so it does not
 * check that the final day or a group-day slot is there: build the payload with
 * `buildAvailabilityFromSelection` and check it with `getMissingAvailabilityMessage`.
 */
export const updateTeamAvailabilityAsManager = async (
  tournamentId: string,
  registrationId: string,
  availability: Availability[],
): Promise<void> => {
  await api.put(
    `/tournaments/${tournamentId}/registrations/${registrationId}/availability`,
    availability,
    { params: { fields: 'id' } },
  );
};

/**
 * The manager records a payment a player made outside the app (bank transfer or cash handed over
 * at the club). The backend books it through the cash gateway, so the GetMatch fee lands on the
 * organizer's current-account debt exactly like any other cash charge.
 *
 * Only valid on INDIVIDUAL registrations that still have a pending payment.
 */
export const markPlayerAsPaid = async (
  tournamentId: string,
  registrationId: string,
  playerEmail: string,
): Promise<void> => {
  await api.post(
    `/tournaments/${tournamentId}/registrations/${registrationId}/mark-paid`,
    null,
    { params: { playerEmail, fields: 'id' } },
  );
};

/**
 * Undoes a payment the manager recorded by hand. Rejected by the backend for MercadoPago payments:
 * those move real money and are reversed through the cancellation flows instead.
 */
export const undoPlayerPayment = async (
  tournamentId: string,
  registrationId: string,
  playerEmail: string,
): Promise<void> => {
  await api.delete(
    `/tournaments/${tournamentId}/registrations/${registrationId}/mark-paid`,
    { params: { playerEmail, fields: 'id' } },
  );
};

export default api;
